import type { QuestionFill as QuestionFillT } from '@repo/lib/quiz/questions/class/QuestionFill';
import type { GapQuestionFill } from '@repo/types/questions/QuestionFill';

import { fillGapRegex } from '@repo/schemas/utils/fillGap';

import type { QuestionFillProps } from '@/components/features/quiz/question/question-resolver/question-fill';

import { useQuestionState } from '@/hooks/questions';

export function useQuestionFill(questionFillProps: QuestionFillProps) {
  const { question, questionIndex, sectionIndex } = questionFillProps;

  const { getContent, setAnswer, getAnswer, getMarked, exportedAnswer } = useQuestionState<QuestionFillT>(question, sectionIndex, questionIndex);

  const marked = getMarked();
  const userAnswer = getAnswer();
  const exportedUserAnswer = exportedAnswer();

  const onGapValueChange = (newValue: string, gapId: string) => {
    const questionContent = getContent();
    const allGapIds: string[] = questionContent.gaps.map((gap) => gap.id);
    const answers = getAnswer() || {};
    const updatedAnswers = { ...answers };
    allGapIds.forEach((id) => {
      if (!(id in updatedAnswers)) {
        updatedAnswers[id] = null;
      }
    });

    updatedAnswers[gapId] = newValue.trim();
    setAnswer(updatedAnswers);
  };

  const getGapContent = (slice: string): string => {
    return slice.replace(/^\[|\]$/g, '').trim();
  };

  const createGaps = () => {
    const questionContent = getContent();
    const questionAnswers = getAnswer();
    const gapsContentPartials: string[] = questionContent.gapsContent.split(fillGapRegex).filter(Boolean);

    const data = gapsContentPartials.map((slice: string) => {
      const gapContent: string = getGapContent(slice);
      const gapData: GapQuestionFill | undefined = questionContent.gaps.find((gap) => gap.id === gapContent) ?? undefined;

      return {
        content: gapContent,
        gap: gapData,
        value: questionAnswers ? (questionAnswers[gapContent] ?? undefined) : undefined,
      };
    });
    return data;
  };
  const getMarkedState = (gap?: GapQuestionFill): boolean | undefined => {
    if (!gap || !userAnswer || !marked) return undefined;
    const userAnswerEntries = Object.entries(userAnswer);
    const markedEntries = Object.entries(marked);
    if (userAnswerEntries.map((a) => a[0]).includes(gap.id)) {
      return markedEntries.find((a) => a[0] === gap.id)?.[1]?.correct ?? undefined;
    }
    return undefined;
  };

  return {
    marked,
    gaps: createGaps(),
    onGapValueChange,
    getMarkedState,
    exportedUserAnswer,
  };
}
